import React, { useEffect, useRef } from 'react';

const HeroSection = () => {
  const canvasRef = useRef(null);
  const cursorRef = useRef(null);
  
  // 初始化iOS 26风格的液态玻璃鼠标特效
  useEffect(() => {
    // 创建DOM元素
    const cursor = document.createElement('div');
    cursor.id = 'ios-cursor';
          cursor.style.cssText = `
      position: fixed;
      width: 32px;
      height: 32px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 50%;
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      pointer-events: none;
      z-index: 9999;
      transform: translate(-50%, -50%);
      transition: width 0.2s, height 0.2s, border-radius 0.2s, background 0.2s;
      box-shadow: 0 0 10px rgba(255, 255, 255, 0.1), inset 0 0 8px rgba(255, 255, 255, 0.2);
      display: none;
    `;
    document.body.appendChild(cursor);
    cursorRef.current = cursor;

    // 创建光晕效果
    const glow = document.createElement('div');
    glow.id = 'ios-cursor-glow';
    glow.style.cssText = `
      position: fixed;
      width: 60px;
      height: 60px;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 70%);
      border-radius: 50%;
      pointer-events: none;
      z-index: 9998;
      transform: translate(-50%, -50%);
      display: none;
    `;
    document.body.appendChild(glow);

    // 鼠标位置和目标位置
    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;
    
    // 鼠标移动事件处理
    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      // 显示光标
      if (cursor.style.display === 'none') {
        cursor.style.display = 'block';
        glow.style.display = 'block';
      }
      
      // 检测是否在Hero部分
      const heroSection = document.getElementById('hero');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          // 在Hero部分内，应用特殊效果
          cursor.style.background = 'rgba(255, 255, 255, 0.15)';
          cursor.style.backdropFilter = 'blur(4px)';
        } else {
          // 不在Hero部分内，恢复正常
          cursor.style.background = 'rgba(255, 255, 255, 0.15)';
          cursor.style.backdropFilter = 'blur(4px)';
        }
      }
      
      // 检测鼠标下方的元素
      const element = document.elementFromPoint(e.clientX, e.clientY);
      
      // 磁吸效果 - 检测可点击元素
      if (element) {
        // 获取可点击元素（如果存在）
        let clickableElement = null;
        
        if (element.tagName === 'BUTTON' || element.tagName === 'A') {
          clickableElement = element;
        } else {
          clickableElement = element.closest('button') || element.closest('a');
        }
        
        if (clickableElement) {
          // 获取元素位置
          const rect = clickableElement.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          
          // 计算距离
          const distance = Math.sqrt(
            Math.pow(mouseX - centerX, 2) + 
            Math.pow(mouseY - centerY, 2)
          );
          
          // 如果距离小于100px，应用磁吸效果
          if (distance < 100) {
            // 计算吸引力（距离越近吸引力越大）
            const attraction = 0.3 * (1 - distance / 100);
            
            // 更新目标位置（部分移向按钮）
            targetX = mouseX + (centerX - mouseX) * attraction;
            targetY = mouseY + (centerY - mouseY) * attraction;
            
            // 放大光标
            cursor.style.width = '40px';
            cursor.style.height = '40px';
            cursor.style.background = 'rgba(255, 255, 255, 0.2)';
            
            return;
          }
        }
      }
      
      // 默认情况：目标位置就是鼠标位置
      targetX = mouseX;
      targetY = mouseY;
      
      // 恢复光标默认大小
      cursor.style.width = '32px';
      cursor.style.height = '32px';
    };
    
    // 鼠标离开页面
    const handleMouseLeave = () => {
      cursor.style.display = 'none';
      glow.style.display = 'none';
    };
    
    // 动画更新
    const updateCursor = () => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${targetX}px`;
        cursorRef.current.style.top = `${targetY}px`;
        
        // 更新光晕位置
        const glow = document.getElementById('ios-cursor-glow');
        if (glow) {
          glow.style.left = `${targetX}px`;
          glow.style.top = `${targetY}px`;
        }
      }
      
      requestAnimationFrame(updateCursor);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    updateCursor();
    
    return () => {
      if (cursorRef.current) {
        document.body.removeChild(cursorRef.current);
      }
      if (document.getElementById('ios-cursor-glow')) {
        document.body.removeChild(document.getElementById('ios-cursor-glow'));
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* 背景图片和渐变蒙版 - 使用优化的WebP格式 */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url("./images/common/hero/tech-bg.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-r from-dark/90 via-dark/80 to-dark/70 z-10"></div>
      
      <div className="container mx-auto px-6 md:px-12 py-20 relative z-20">
        <div className="flex flex-col md:flex-row items-center">
          {/* 左侧 - 文本内容 */}
          <div className="w-full md:w-3/5 text-white space-y-8 mb-12 md:mb-0">
            <div className="inline-block bg-primary/20 backdrop-blur-sm px-4 py-1 rounded-full text-sm font-medium text-blue-300 mb-2">
              科技创新 · 智能应用 · 便捷生活
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="block mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
                引力结构
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl text-white/90">
                让每一次交互都充满惊喜
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-xl leading-relaxed">
              我们专注于创造简单易用的AI应用，将复杂技术转化为直观体验，让科技真正走入生活的每个角落
            </p>
            <div className="pt-6 flex flex-wrap gap-4">
              <a 
                href="#products" 
                className="inline-block bg-primary hover:bg-secondary text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/30"
              >
                探索产品
              </a>
              <a 
                href="#about" 
                className="inline-block bg-transparent border border-white/30 hover:border-white/60 text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 backdrop-blur-sm"
              >
                了解我们
              </a>
            </div>
          </div>
          
          {/* 右侧 - 科技视觉 */}
          <div className="w-full md:w-2/5 flex justify-center">
            <div className="relative w-full max-w-md">
              {/* 浮动元素 */}
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <div className="w-72 h-72 rounded-full bg-blue-500/20 backdrop-blur-sm animate-pulse"></div>
              </div>
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <div className="w-56 h-56 rounded-full bg-cyan-400/20 backdrop-blur-sm animate-ping" style={{ animationDuration: '4s' }}></div>
              </div>
              <div className="absolute top-10 right-10 w-20 h-20 rounded-xl bg-blue-300/30 backdrop-blur-sm animate-bounce" style={{ animationDuration: '2.5s' }}></div>
              <div className="absolute bottom-10 left-10 w-16 h-16 rounded-lg bg-indigo-400/30 backdrop-blur-sm animate-bounce" style={{ animationDuration: '3.5s' }}></div>
              
              {/* 中心图标 - 改为圆形 */}
              <div className="relative z-10 w-full h-80 md:h-96 flex items-center justify-center">
                <div className="p-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-xl">
                  <svg className="w-28 h-28 text-blue-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 装饰元素 */}
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-dark to-transparent z-10"></div>
    </section>
  );
};

export default HeroSection; 